import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./modal.scss";

export interface ModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: "sm" | "md" | "lg" | "xl";
  /** When true, clicking the overlay or pressing Escape will NOT close the modal. */
  backdrop?: "static" | true;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  bodyClassName?: string;
}

export function Modal({
  show,
  onClose,
  title,
  subtitle,
  size = "md",
  backdrop,
  children,
  footer,
  bodyClassName,
}: ModalProps) {
  const isStatic = backdrop === "static";

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isStatic) onClose();
    },
    [onClose, isStatic],
  );

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  return createPortal(
    <div className="rd-modal-overlay" onClick={isStatic ? undefined : onClose}>
      <div
        className={`rd-modal rd-modal--${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="rd-modal__close" onClick={onClose} type="button">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path
              d="M1 1L14 14M14 1L1 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="rd-modal__header">
          {subtitle && <span className="rd-modal__subtitle">{subtitle}</span>}
          <h2 className="rd-modal__title">{title}</h2>
        </div>

        <div className={`rd-modal__body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>

        {footer && <div className="rd-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
